import React from 'react';
import PropTypes from 'prop-types';

/**
 * UserForm - Form for creating/editing portal users
 *
 * Handles all user input fields and submission
 *
 * @param {Object} props
 * @param {Object} props.formData - Current form state
 * @param {boolean} props.isEditing - Whether in edit mode
 * @param {Object} props.selectedUser - Currently selected user
 * @param {Array} props.districts - Available districts
 * @param {Array} props.authoritiesSelection - Selected authorities
 * @param {Object} props.permissions - Permission label map
 * @param {Array} props.subAreas - Available sub-areas
 * @param {Array} props.selectedSubAreaIds - Selected sub-area IDs
 * @param {boolean} props.showSubAreaSelection - Whether to show the sub-area selector
 * @param {Function} props.onInputChange - Input change handler
 * @param {Function} props.onSubmit - Form submit handler
 * @param {Function} props.onCancel - Cancel/reset handler
 * @param {Function} props.onToggleAuthority - Authority toggle handler
 * @param {Function} props.onAssignAuthorities - Assign authorities handler
 * @param {Function} props.onReplaceAuthorities - Replace authorities handler
 */
const UserForm = ({
  formData,
  isEditing,
  selectedUser,
  districts,
  authoritiesSelection,
  permissions,
  subAreas,
  selectedSubAreaIds,
  showSubAreaSelection,
  onInputChange,
  onSubmit,
  onCancel,
  onToggleAuthority,
  onAssignAuthorities,
  onReplaceAuthorities,
  onSubAreaSelectionChange,
}) => {
  return (
    <div
      className="section-card"
      style={isEditing ? { border: '2px solid #3182ce' } : {}}
    >
      <div className="section-header">
        <h2>
          {isEditing ? '✏️ Edit User' : 'Create New User'}
          {isEditing && selectedUser && (
            <span
              style={{ fontSize: '14px', color: '#718096', marginLeft: '12px' }}
            >
              (Editing: {selectedUser.userName})
            </span>
          )}
        </h2>
        {isEditing && (
          <button className="btn-cancel" onClick={onCancel}>
            Cancel Edit
          </button>
        )}
      </div>

      <form onSubmit={onSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="userName" className="required">
              Username
            </label>
            <input
              type="text"
              id="userName"
              name="userName"
              value={formData.userName}
              onChange={onInputChange}
              required
              disabled={isEditing}
              placeholder="Enter username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className={!isEditing ? 'required' : ''}>
              Password {isEditing && '(leave blank to keep current)'}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={onInputChange}
              required={!isEditing}
              placeholder="Enter password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="userFirst" className="required">
              First Name
            </label>
            <input
              type="text"
              id="userFirst"
              name="userFirst"
              value={formData.userFirst}
              onChange={onInputChange}
              required
              placeholder="Enter first name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="userLast" className="required">
              Last Name
            </label>
            <input
              type="text"
              id="userLast"
              name="userLast"
              value={formData.userLast}
              onChange={onInputChange}
              required
              placeholder="Enter last name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="required">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={onInputChange}
              required
              placeholder="Enter email address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="permission" className="required">
              Permission Level
            </label>
            <select
              id="permission"
              name="permission"
              value={formData.permission}
              onChange={onInputChange}
              required
            >
              {Object.entries(permissions).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {isEditing && (
            <div className="form-group">
              <label>Authorities (multi-role)</label>
              <div className="checkbox-group">
                {['Admin', 'Office', 'DM', 'AS'].map((role) => (
                  <label key={role} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={authoritiesSelection.includes(role)}
                      onChange={() => onToggleAuthority(role)}
                    />
                    {role}
                  </label>
                ))}
              </div>
              <div className="inline-actions" style={{ marginTop: '8px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onAssignAuthorities}
                >
                  Assign Roles (Add)
                </button>
                <button
                  type="button"
                  className="btn-warning"
                  onClick={onReplaceAuthorities}
                  style={{ marginLeft: '8px' }}
                >
                  Replace Roles (Overwrite)
                </button>
              </div>
              <small className="hint-text">
                Assign adds to existing roles; Replace overwrites the user's
                roles.
              </small>
            </div>
          )}

          {showSubAreaSelection && (
            <div className="form-group">
              <label htmlFor="assignedSubAreas">
                Assigned Sub-Areas{' '}
                <span style={{ color: '#718096', fontWeight: 400 }}>
                  (required for AS)
                </span>
              </label>
              <select
                id="assignedSubAreas"
                name="assignedSubAreas"
                multiple
                value={selectedSubAreaIds}
                onChange={onSubAreaSelectionChange}
              >
                {subAreas.map((subArea) => (
                  <option key={subArea.id} value={subArea.id}>
                    {subArea.name}
                    {subArea.district?.districtName
                      ? ` (${subArea.district.districtName})`
                      : ''}
                  </option>
                ))}
              </select>
              <small className="hint-text">
                Hold Ctrl (Windows) or Cmd (Mac) to select multiple sub-areas.
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="district">Districts</label>
            <select
              id="district"
              name="district"
              multiple
              value={
                Array.isArray(formData.district)
                  ? formData.district.map((d) => d.districtName)
                  : []
              }
              onChange={onInputChange}
            >
              {districts.map((d, idx) => (
                <option key={d.id || idx} value={d.districtName}>
                  {d.districtName}
                </option>
              ))}
            </select>
            <small className="hint-text">
              Hold Ctrl (Windows) or Cmd (Mac) to select multiple districts.
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-submit">
            {isEditing ? 'Update User' : 'Create User'}
          </button>
          {isEditing && (
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Clear Form
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

UserForm.propTypes = {
  formData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    userName: PropTypes.string,
    password: PropTypes.string,
    userFirst: PropTypes.string,
    userLast: PropTypes.string,
    email: PropTypes.string,
    permission: PropTypes.string,
    district: PropTypes.array,
  }).isRequired,
  isEditing: PropTypes.bool.isRequired,
  selectedUser: PropTypes.object,
  districts: PropTypes.array,
  authoritiesSelection: PropTypes.array,
  permissions: PropTypes.object,
  subAreas: PropTypes.array,
  selectedSubAreaIds: PropTypes.array,
  showSubAreaSelection: PropTypes.bool,
  onInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onToggleAuthority: PropTypes.func,
  onAssignAuthorities: PropTypes.func,
  onReplaceAuthorities: PropTypes.func,
  onSubAreaSelectionChange: PropTypes.func,
};

UserForm.defaultProps = {
  selectedUser: null,
  districts: [],
  authoritiesSelection: [],
  permissions: {},
  subAreas: [],
  selectedSubAreaIds: [],
  showSubAreaSelection: false,
  onToggleAuthority: () => {},
  onAssignAuthorities: () => {},
  onReplaceAuthorities: () => {},
  onSubAreaSelectionChange: () => {},
};

export default UserForm;
